import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { postData } from "../utility/Utility";
import { toast } from "react-toastify";
import ReactPaginate from 'react-paginate';

export default function AgentSelectionModal({ show, onClose, onSelect }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [cityFilterEnabled, setCityFilterEnabled] = useState(false);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const rowsPerPage = 10;

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const response = await postData("api/users/list", {
        role: "agent",
        limit: rowsPerPage,
        page: currentPage + 1,
        search: searchTerm.trim(),
        cityFilter: cityFilterEnabled
      });

      if (response.success) {
        setAgents(response.data?.users || []);
        setTotalPages(response.data?.totalPages || 1);
      } else {
        toast.error(response.message || "Failed to fetch agents");
      }
    } catch (error) {
      toast.error("Error fetching agents");
      console.error("Fetch agents error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (show) {
        fetchAgents();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [show, searchTerm, cityFilterEnabled, currentPage]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchAgents();
  };

  const toggleCityFilter = (e) => {
    setCityFilterEnabled(e.target.checked);
    if (!e.target.checked) {
      setSearchTerm("");
    }
    setCurrentPage(0);
  };

  const handleReset = () => {
    setSearchTerm("");
    setCityFilterEnabled(false);
    setCurrentPage(0);
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Select Agent</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="card mb-3">
          <div className="card-body">
            <form onSubmit={handleSubmit} className="row g-3">
              <div className="col-md-2">
                <div className="form-check form-switch d-flex align-items-center h-100">
                  <input
                    className="form-check-input me-2"
                    type="checkbox"
                    id="cityFilterToggle"
                    checked={cityFilterEnabled}
                    onChange={toggleCityFilter}
                  />
                  <label className="form-check-label" htmlFor="cityFilterToggle">
                    City Filter
                  </label>
                </div>
              </div>
              <div className="col-md-4">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={cityFilterEnabled ? "Search disabled when city filter is active" : "Search by name, email or mobile..."}
                    value={searchTerm}
                    onChange={(e) => !cityFilterEnabled && setSearchTerm(e.target.value)}
                    readOnly={cityFilterEnabled}
                  />
                  <span className="input-group-text">
                    <i className={`mdi mdi-magnify ${cityFilterEnabled ? "text-muted" : ""}`}></i>
                  </span>
                </div>
              </div>
              <div className="col-md-3 d-flex gap-2">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  Search
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleReset} disabled={loading}>
                  Reset
                </button>
              </div>
            </form>
          </div>
        </div>
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : agents.length > 0 ? (
          <>
            <div className="table-responsive">
              <table className="table table-hover table-centered mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '60px' }}>S.No</th>
                    <th>Agent Details</th>
                    <th>Contact</th>
                    <th>Location</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent, index) => (
                    <tr key={agent._id}>
                      <td className="text-center">
                        {currentPage * rowsPerPage + index + 1}
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          {agent.image ? (
                            <img 
                              src={agent.image} 
                              className="rounded-circle me-2" 
                              width="40" 
                              height="40" 
                              alt={agent.fullName}
                            />
                          ) : (
                            <div className="avatar-xs me-2">
                              <div className="avatar-title rounded-circle bg-light text-primary">
                                {agent.fullName?.charAt(0) || 'A'}
                              </div>
                            </div>
                          )}
                          <div>
                            <h6 className="mb-0">{agent.fullName}</h6>
                            <small className="text-muted">
                              ID: {agent.uniqueId || agent._id}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div>{agent.mobile}</div>
                          <small className="text-muted">{agent.email}</small>
                        </div>
                      </td>
                      <td>
                        {agent.location ? (
                          <div className="d-flex align-items-center">
                            <i className="mdi mdi-map-marker-outline me-1 text-danger"></i>
                            <div className="d-flex flex-column">
                              <span className="text-truncate" style={{ maxWidth: '150px' }}>
                                {agent.location.city || ''}
                                {agent.location.city && agent.location.state ? ', ' : ''}
                                {agent.location.state || ''}
                              </span>
                              {agent.location.coordinates && (
                                <a
                                  href={`https://www.google.com/maps?q=${agent.location.coordinates[1]},${agent.location.coordinates[0]}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary small d-flex align-items-center"
                                  style={{ fontSize: '0.75rem' }}
                                >
                                  <i className="mdi mdi-map-marker-radius me-1"></i>
                                  View on Map
                                </a>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted small">N/A</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => onSelect(agent)}
                        >
                          Select
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <ReactPaginate
                previousLabel={<i className="mdi mdi-chevron-left"></i>}
                nextLabel={<i className="mdi mdi-chevron-right"></i>}
                breakLabel="..."
                breakClassName="page-item"
                breakLinkClassName="page-link"
                pageCount={totalPages}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={handlePageChange}
                containerClassName="pagination pagination-rounded justify-content-end mt-3"
                pageClassName="page-item"
                pageLinkClassName="page-link"
                previousClassName="page-item"
                previousLinkClassName="page-link"
                nextClassName="page-item"
                nextLinkClassName="page-link"
                activeClassName="active"
                forcePage={currentPage}
              />
            )}
          </>
        ) : (
          <div className="text-center py-4">No agents found</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
